import { useCallback } from 'react';

const STYLE_CACHE = new Map();
let STYLE_SHEET = null;

const UNITLESS_PROPERTIES = new Set([
  'animationIterationCount',
  'aspectRatio',
  'borderImageOutset',
  'borderImageSlice',
  'borderImageWidth',
  'columnCount',
  'columns',
  'flex',
  'flexGrow',
  'flexShrink',
  'fontWeight',
  'gridArea',
  'gridColumn',
  'gridColumnEnd',
  'gridColumnStart',
  'gridRow',
  'gridRowEnd',
  'gridRowStart',
  'lineHeight',
  'opacity',
  'order',
  'orphans',
  'scale',
  'widows',
  'zIndex',
  'zoom',
]);

const getStyleSheet = () => {
  if (typeof document === 'undefined') {
    return null;
  }

  if (STYLE_SHEET) {
    return STYLE_SHEET;
  }

  const styleTag = document.createElement('style');
  styleTag.setAttribute('data-runtime-css', 'true');
  document.head.appendChild(styleTag);
  STYLE_SHEET = styleTag.sheet;

  return STYLE_SHEET;
};

const toKebabCase = (property) => {
  if (property.startsWith('--')) {
    return property;
  }

  return property
    .replace(/^ms/, '-ms')
    .replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`);
};

const normalizeValue = (property, value) => {
  if (value === null || value === undefined || value === false) {
    return null;
  }

  if (typeof value === 'number' && value !== 0 && !UNITLESS_PROPERTIES.has(property)) {
    return `${value}px`;
  }

  return String(value);
};

const serializeStyle = (styleObject) => {
  const entries = Object.entries(styleObject || {})
    .filter(([, value]) => value !== undefined && value !== null && value !== false)
    .sort(([a], [b]) => a.localeCompare(b));

  return entries
    .map(([property, value]) => {
      const normalizedValue = normalizeValue(property, value);
      if (normalizedValue === null) {
        return null;
      }

      return `${toKebabCase(property)}:${normalizedValue};`;
    })
    .filter(Boolean)
    .join('');
};

const hashString = (input) => {
  let hash = 5381;

  for (let index = 0; index < input.length; index += 1) {
    hash = (hash * 33) ^ input.charCodeAt(index);
  }

  return (hash >>> 0).toString(36);
};

const ensureClassForStyle = (styleObject) => {
  const serializedStyle = serializeStyle(styleObject);

  if (!serializedStyle) {
    return '';
  }

  if (STYLE_CACHE.has(serializedStyle)) {
    return STYLE_CACHE.get(serializedStyle);
  }

  const className = `rt-${hashString(serializedStyle)}`;
  const sheet = getStyleSheet();

  if (sheet) {
    sheet.insertRule(`.${className}{${serializedStyle}}`, sheet.cssRules.length);
  }

  STYLE_CACHE.set(serializedStyle, className);
  return className;
};

export const cx = (...values) =>
  values
    .flatMap((value) => (Array.isArray(value) ? value : [value]))
    .filter(Boolean)
    .join(' ');

export const useRuntimeCss = () =>
  useCallback((styleObject) => ensureClassForStyle(styleObject), []);

